import { cloneElement, forwardRef, type PropsWithChildren, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '../hooks/useAnalytics';
import { getCleanLocationHref } from '../lib/urlUtils';
import { warnOnDevIfCloneElementWillFail } from '../lib/utils';
import { EventName, type LinkViewedPayload } from '../Shibuya';
import { useAnalyticsPageView } from './PageView/Context';

export interface AnalyticsLinkViewedProps {
  destinationUrl: LinkViewedPayload['destination_url'];
  event_id: LinkViewedPayload['event_id'];
  linkCopy: LinkViewedPayload['link_copy'];
  scrollableAncestor?: HTMLElement;
}

export const AnalyticsLinkViewed = forwardRef<
  HTMLElement,
  PropsWithChildren<AnalyticsLinkViewedProps>
>(({ children, scrollableAncestor, ...props }, ref) => {
  const { analytics, deployableName, deployableVersion, gitSha } = useAnalytics();
  const { pageViewId, pageViewReady } = useAnalyticsPageView();
  const { ref: inViewRef, inView } = useInView({
    root: scrollableAncestor ? scrollableAncestor || 'window' : null,
    threshold: 0.01,
    triggerOnce: true,
  });

  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        ref.current = node;
      }
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    if (pageViewReady && inView) {
      const eventPayload: LinkViewedPayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        destination_url: props.destinationUrl,
        entity_name: 'link',
        event_id: props.event_id,
        impression_pageview_id: pageViewId,
        impression_page_url: getCleanLocationHref(),
        link_copy: props.linkCopy,
      };

      analytics?.track(EventName.LINK_VIEWED, eventPayload, {
        context: {
          app: {
            name: deployableName,
            version: gitSha,
          },
        },
      });
    }
  }, [pageViewReady, inView]);

  if (!children) {
    throw new Error('Missing Child');
  }

  warnOnDevIfCloneElementWillFail(children, 'AnalyticsLinkViewed');
  return cloneElement(children as React.ReactElement<{ ref: unknown }>, {
    ref: setRefs,
  });
});
